/**
 * The theme guard: verify the *rendered* result, and roll back if it fails.
 *
 * Taken from `emrx` (`docs/comparison-emrx.md` §5.1, "Guards reject and roll back, and do not
 * persist"). The property that matters is not that a check exists but that a failing theme is not
 * remembered: without it, one bad brand colour can leave a practice unable to read its own charts
 * until someone with database access intervenes, and a reload does not clear it.
 *
 * Today the only thing a user can change is an accent *name* from a measured allowlist
 * (`palette.ts`), and `palette.test.ts` already proves every one of those combinations clears AA.
 * So this guard currently cannot fail on the shipped palette — which is exactly why it is written
 * now rather than later. `claude-kit/rules/invariants.md` requires a commercial product to let the
 * paying practice supply its own brand colour, and that input **cannot** be enumerated in advance;
 * growing the guard alongside the theming is far cheaper than retrofitting one underneath theming
 * that already ships. The check runs on every apply, so the day an arbitrary hex reaches it, it is
 * already load-bearing.
 *
 * Two design points, both learned from things that went wrong elsewhere:
 *
 *   - **It measures computed values, not declared ones.** A `var()` chain that resolves to nothing
 *     produces an empty string, and a check reading the declaration it just wrote would pass while
 *     the page renders unstyled. `readComputedToken` is injected so this module stays testable and
 *     so the only DOM access is at the boundary.
 *   - **An unreadable value is a failure, not a skip.** `contrastRatio` returns `null` for a colour
 *     it cannot parse, and every such case is reported as a problem. A check that silently passes
 *     on the input it does not understand is the "measurement that can only report success" this
 *     project's own working-style rules name.
 */

import { AA_LARGE, AA_TEXT, contrastRatio } from './contrast.ts';

/** Reads the computed value of a CSS custom property. Injected so this module needs no DOM. */
export type TokenReader = (token: string) => string;

export interface ThemeProblem {
  /** The pair that failed, named as tokens so a report says which declaration to change. */
  readonly foreground: string;
  readonly background: string;
  /** The measured ratio, or `null` when a value could not be parsed at all. */
  readonly ratio: number | null;
  readonly required: number;
}

export interface ThemeCheck {
  readonly ok: boolean;
  readonly problems: readonly ThemeProblem[];
}

interface Pair {
  readonly foreground: string;
  readonly background: string;
  readonly required: number;
}

/**
 * The pairs a theme must not break.
 *
 * Deliberately short. Every pair here is one a user reads on essentially every screen, so a
 * failure is a real legibility incident rather than a lint finding — and a long list would make
 * the guard slow enough that someone eventually turns it off.
 */
const REQUIRED_PAIRS: readonly Pair[] = [
  { foreground: '--s-ink', background: '--s-canvas', required: AA_TEXT },
  { foreground: '--s-ink', background: '--s-surface', required: AA_TEXT },
  { foreground: '--s-ink-muted', background: '--s-surface', required: AA_TEXT },
  { foreground: '--s-accent-text', background: '--s-surface', required: AA_TEXT },
  { foreground: '--s-accent-ink', background: '--s-accent', required: AA_TEXT },
  { foreground: '--s-danger', background: '--s-surface', required: AA_TEXT },
  // The environment banner. A safety control rather than chrome: this is the bar that says a
  // "test" environment holds real patient data. A theme that made it unreadable would remove a
  // warning, not spoil an aesthetic, so it is checked on every apply like the body text is.
  { foreground: '--s-notice-danger-ink', background: '--s-notice-danger-bg', required: AA_TEXT },
  { foreground: '--s-notice-info-ink', background: '--s-notice-info-bg', required: AA_TEXT },
  // Non-text: a focus ring and a field boundary only have to be *findable*, not readable.
  { foreground: '--s-focus', background: '--s-canvas', required: AA_LARGE },
  { foreground: '--ui-field-line', background: '--ui-field-bg', required: AA_LARGE },
];

/**
 * Check the currently-applied theme.
 *
 * Returns every problem rather than the first, so a rejection message can say what is wrong with
 * a submitted brand colour in one round trip instead of one failure at a time.
 */
export function verifyTheme(read: TokenReader): ThemeCheck {
  const problems: ThemeProblem[] = [];

  for (const pair of REQUIRED_PAIRS) {
    const ratio = contrastRatio(read(pair.foreground), read(pair.background));
    if (ratio === null || ratio < pair.required) {
      problems.push({
        foreground: pair.foreground,
        background: pair.background,
        ratio,
        required: pair.required,
      });
    }
  }

  return { ok: problems.length === 0, problems };
}

/** A one-line, human-readable account of a failed check. Used in the visible rejection notice. */
export function describeThemeCheck(check: ThemeCheck): string {
  if (check.ok) return 'Theme passed every contrast check.';
  return check.problems
    .map((problem) => {
      const measured = problem.ratio === null ? 'unreadable' : `${problem.ratio.toFixed(2)}:1`;
      return `${problem.foreground} on ${problem.background} is ${measured}, needs ${problem.required.toFixed(1)}:1`;
    })
    .join('; ');
}
